import { memo, Suspense, useCallback, useContext, useEffect } from 'react'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { OVERVIEW_ROUTE, PRODUCTS_ROUTE, usePerformanceMeasure } from '@ucc/unified-admin-common'

import { PERFORMANCE_MARKS, PERFORMANCE_MEASURES } from './logger/performanceConstants'
import { UnleashContext } from './providers/UnleashProvider'
import useBillingRoutes from './routes/useBillingRoutes'
import useCCConfigurationRoutes from './routes/useCCConfigurationRoutes'
import useContactsRoutes from './routes/useContactsRoutes'
import useCoreRoutes from './routes/useCoreRoutes'
import useDashboardRoutes from './routes/useDashboardRoutes'
import useDevicesRoutes from './routes/useDevicesRoutes'
import useDialPlanRoutes from './routes/useDialPlanRoutes'
import useGtcBillingRoutes from './routes/useGtcBillingRoutes'
import useInboxRoutes from './routes/useInboxRoutes'
import useIntegrationsRoutes from './routes/useIntegrationsRoutes'
import useLaunchpadRoutes from './routes/useLaunchpadRoutes'
import useLocationsRoutes from './routes/useLocationsRoutes'
import useMediaRoutes from './routes/useMediaRoutes'
import useNetworkMeasurementsRoutes from './routes/useNetworkMeasurementsRoutes'
import useOrderingRoutes from './routes/useOrderingRoutes'
import useProductsExplorationRoutes from './routes/useProductsExplorationRoutes'
import useSmsCampaignRoutes from './routes/useSmsCampaignRoutes'
import useTenantsRoutes from './routes/useTenantsRoutes'
import useVirtualReceptionistRoutes from './routes/useVirtualReceptionistRoutes'
import useVoiceRoutes from './routes/useVoiceRoutes'
import useZeroTrustRoutes from './routes/useZeroTrustRoutes'

const Routes = (): JSX.Element => {
  // Those routes are mainly for sub-apps
  const { path } = useRouteMatch()
  const { measureFromStartMarkOnFirstMount } = usePerformanceMeasure()
  const { gotoLaunchpadEnabled } = useContext(UnleashContext)

  const onUnmountComponent = useCallback(() => {
    measureFromStartMarkOnFirstMount(
      PERFORMANCE_MEASURES.WRAPPER_INITIAL_LOADING_TIME,
      PERFORMANCE_MARKS.WRAPPER_INITIAL_LOAD_START_MARK,
    )
  }, [measureFromStartMarkOnFirstMount])

  useEffect(() => {
    return onUnmountComponent()
  }, [onUnmountComponent])

  //Note: The order of the routes is important mainly if the routes prefix are the same, the first match will be rendered.
  //      Sub-apps routes should be direct children of the Switch component
  const routes = [
    useOrderingRoutes(path),
    useMediaRoutes(path),
    useSmsCampaignRoutes(path),
    useLocationsRoutes(path),
    useVoiceRoutes(path),
    useNetworkMeasurementsRoutes(path),
    useDevicesRoutes(path),
    useDialPlanRoutes(path),
    ...useInboxRoutes(path),
    useZeroTrustRoutes(path),
    useCoreRoutes(path),
    useGtcBillingRoutes(path),
    useVirtualReceptionistRoutes(path),
    useDashboardRoutes(path),
    useProductsExplorationRoutes(path),
    useIntegrationsRoutes(path),
    useContactsRoutes(path),
    useBillingRoutes(path),
    useTenantsRoutes(path),
    ...useCCConfigurationRoutes(path),
    useLaunchpadRoutes(path),
  ]

  return (
    <Suspense fallback={<></>}>
      <Switch>
        {gotoLaunchpadEnabled && <Redirect exact from={`${path}/${PRODUCTS_ROUTE}`} to={`${path}/${OVERVIEW_ROUTE}`} />}
        {!gotoLaunchpadEnabled && (
          <Redirect exact from={`${path}/${OVERVIEW_ROUTE}`} to={`${path}/${PRODUCTS_ROUTE}`} />
        )}
        {routes.map((route) =>
          route.paths.length > 0 ? (
            <Route path={route.paths} key={route.paths.toString()}>
              {route.component}
            </Route>
          ) : null,
        )}
        <Redirect to={{ pathname: '/' }} />
      </Switch>
    </Suspense>
  )
}

export default memo(Routes)
